import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { forwardRef, useImperativeHandle } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";
import "./RegistrationForm.css";
import { useFormContext } from "./RegistrationFormContext";
import DropdownWithCustomValuesModal from "../../components/DropdownWithCustomValuesModal";
import StepSuccessModal from "./StepSuccessModal";

const Step4PrevSchools = forwardRef(({ isOpen, moveToNextStep }, ref) => {
  // const { currentStep } = props;
  const token = localStorage.getItem("token");
  const roleId = localStorage.getItem("roleId");
  const { sharedData } = useFormContext();
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const {
    register,
    control,
    setValue,
    getValues,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      studentFirstRegistrationId: "",
      studentPreviousSchools: [
        {
          id: 0,
          createdBy: localStorage.getItem("userId") || "",
          updatedBy: localStorage.getItem("userId") || "",
          schoolId: 0,
          schoolName: "",
          isNotExist: false,
          dateOfEntry: "",
          dateOfLeaving: "",
          otherName: "",
          countryId: "0",
          languageofInstruction: "",
          studentFirstRegistrationId: "",
        },
      ],
      previousSchoolCurriculum: "",
      otherCurriculumn: "",
      educationalSupport: "",
      physicalDifficulties: "",
      priviousSchoolDifficulties: "",
      difficultyDetails: "",
      familyCircumstances: "",
      anySiblingInNunAcademy: "",
    },
  });

  const {
    fields: studentPreviousSchools,
    append: appendPreviousSchoolField,
    remove: removePreviousSchoolField,
    replace,
  } = useFieldArray({
    control,
    name: "studentPreviousSchools",
  });

  const [dataSubmitting, setDataSubmitting] = useState(false);

  const [prevSchoolCurriculumData, setPrevSchoolCurriculumData] = useState([]);
  const [customDropdownName, setCustomDropdownName] = useState("");
  const [
    isCustomDropdownValuesModalVisible,
    setIsCustomDropdownValuesModalVisible,
  ] = useState(false);

  const fetchCommonSettings = async (moduleName, setData) => {
    try {
      const response = await get(
        `CommonSettings/GetCommonSettingDetailByModuleName?moduleName=${moduleName}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setData(response?.commonSettingDetail);
    } catch (error) {
      console.error(`Error fetching ${moduleName}:`, error);
    }
  };

  const handleCustomDropdownSetting = (dropdownName) => {
    setCustomDropdownName(dropdownName);
    setIsCustomDropdownValuesModalVisible(true);
  };

  useEffect(() => {
    fetchCommonSettings(
      "previousSchoolCurriculum",
      setPrevSchoolCurriculumData
    );
  }, [token]);

  const [pupils, setPupils] = useState([]);
  const [selectedPupilId, setSelectedPupilId] = useState(null);
  const [pupilError, setPupilError] = useState(false);
  const [countries, setCountries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await get("Dropdown/GetCountries", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCountries(response);
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };
    fetchCountries();
  }, [token]);

  useEffect(() => {
    if (isOpen && sharedData.userId) {
      fetchPupils();
    }
  }, [isOpen, sharedData.userId]);

  const fetchPupils = async () => {
    setIsLoading(true);
    try {
      console.log("Fetching pupils with userId:", sharedData.userId);
      const response = await get(
        `Student/GetPupilsInformationByUserId?userId=${sharedData.userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      console.log("Fetched pupils:", response);
      setPupils(response || []);

      if (response && response.length > 0) {
        const firstPupilId = response[response.length - 1].id;
        setSelectedPupilId(firstPupilId);
        handlePupilSelection({ target: { value: firstPupilId } });
      } else {
        setSelectedPupilId(null);
      }
    } catch (error) {
      console.error("Error fetching pupils:", error);
      setPupils([]);
      setSelectedPupilId(null);
    } finally {
      setIsLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    save: onSaveForm,
  }));

  const onSaveForm = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    if (!selectedPupilId) {
      setPupilError(true);
      return;
    } else {
      setPupilError(false);
    }

    setDataSubmitting(true);

    const formValues = getValues();

    const validSchools = formValues.studentPreviousSchools
      .filter((school) => school.otherName && school.otherName.trim() !== "")
      .map((school) => ({
        createdBy: school.createdBy || localStorage.getItem("userId") || "",
        updatedBy: school.updatedBy || localStorage.getItem("userId") || "",
        id: school.id || 0,
        schoolId: school.schoolId || 0,
        schoolName: school.schoolName || "",
        isNotExist: school.isNotExist || false,
        dateOfEntry: school.dateOfEntry || null,
        dateOfLeaving: school.dateOfLeaving || null,
        otherName: school.otherName || "",
        countryId: school.countryId || "0",
        studentFirstRegistrationId: selectedPupilId,
        languageofInstruction: school.languageofInstruction || "",
      }));

    const formData = {
      studentFirstRegistrationId: selectedPupilId,
      studentPreviousSchools: validSchools,
      previousSchoolCurriculum: formValues.previousSchoolCurriculum || "",
      otherCurriculumn: formValues.otherCurriculumn || "",
      educationalSupport: formValues.educationalSupport || "",
      physicalDifficulties: formValues.physicalDifficulties || "",
      priviousSchoolDifficulties: formValues.priviousSchoolDifficulties || "",
      difficultyDetails: formValues.difficultyDetails || "",
      familyCircumstances: formValues.familyCircumstances || "",
      anySiblingInNunAcademy: formValues.anySiblingInNunAcademy || "",
    };

    try {
      const response = await post(
        "Student/UpdateAdmissionInqueryPreviousSchoolInformation",
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.isSuccess) {
        setShowSuccessModal(true);
        return true;
      } else {
        toast.error(response.message || "An unexpected error occurred");
        return false;
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to save previous school information.");
      return false;
    } finally {
      setDataSubmitting(false);
    }
  };

  const handlePupilSelection = async (event) => {
    const pupilId = event.target.value;
    setSelectedPupilId(pupilId);
    setPupilError(false);
    if (!pupilId) {
      setPupilError(true);
      replace([
        {
          id: 0,
          createdBy: localStorage.getItem("userId") || "",
          updatedBy: localStorage.getItem("userId") || "",
          schoolId: 0,
          schoolName: "",
          isNotExist: false,
          dateOfEntry: "",
          dateOfLeaving: "",
          otherName: "",
          countryId: "0",
          languageofInstruction: "",
          studentFirstRegistrationId: "",
        },
      ]);
      return;
    }
    try {
      const response = await get(
        `Student/GetPreviousSchoolByFirstRegistrationId?studentFirstRegistrationId=${pupilId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response) {
        const defaultCountryId =
          countries
            .find((country) => country.name.toLowerCase() === "saudi arabia")
            ?.id.toString() ||
          (countries.length > 0 ? countries[0].id.toString() : "0");

        const mappedSchools =
          response?.studentPreviousSchools?.length > 0
            ? response.studentPreviousSchools
                .filter(
                  (school) => school.otherName && school.otherName.trim() !== ""
                )
                .map((school) => ({
                  id: school.id || 0,
                  createdBy:
                    school.createdBy || localStorage.getItem("userId") || "",
                  updatedBy:
                    school.updatedBy || localStorage.getItem("userId") || "",
                  schoolId: school.schoolId || 0,
                  schoolName: school.schoolName || "",
                  isNotExist: school.isNotExist || false,
                  dateOfEntry: school.dateOfEntry
                    ? school.dateOfEntry.split("T")[0]
                    : "",
                  dateOfLeaving: school.dateOfLeaving
                    ? school.dateOfLeaving.split("T")[0]
                    : "",
                  otherName: school.otherName || "",
                  countryId: school.countryId?.toString() || defaultCountryId,
                  studentFirstRegistrationId:
                    school.studentFirstRegistrationId || "",
                  languageofInstruction: school.languageofInstruction || "",
                }))
            : [
                {
                  id: 0,
                  createdBy: localStorage.getItem("userId") || "",
                  updatedBy: localStorage.getItem("userId") || "",
                  schoolId: 0,
                  schoolName: "",
                  isNotExist: false,
                  dateOfEntry: "",
                  dateOfLeaving: "",
                  otherName: "",
                  countryId: defaultCountryId,
                  languageofInstruction: "",
                  studentFirstRegistrationId: "",
                },
              ];

        replace(mappedSchools);

        setValue(
          "previousSchoolCurriculum",
          response.previousSchoolCurriculum || ""
        );
        setValue("otherCurriculumn", response.otherCurriculumn || "");
        setValue("educationalSupport", response.educationalSupport || "");
        setValue("physicalDifficulties", response.physicalDifficulties || "");
        setValue(
          "priviousSchoolDifficulties",
          response.priviousSchoolDifficulties || ""
        );
        setValue("difficultyDetails", response.difficultyDetails || "");
        setValue("familyCircumstances", response.familyCircumstances || "");
        setValue(
          "anySiblingInNunAcademy",
          response.anySiblingInNunAcademy || ""
        );
      }
    } catch (error) {
      console.error("Error fetching previous schools:", error);
      replace([
        {
          id: 0,
          createdBy: localStorage.getItem("userId") || "",
          updatedBy: localStorage.getItem("userId") || "",
          schoolId: 0,
          schoolName: "",
          isNotExist: false,
          dateOfEntry: "",
          dateOfLeaving: "",
          otherName: "",
          countryId: "0",
          languageofInstruction: "",
          studentFirstRegistrationId: "",
        },
      ]);
    }
  };

  const handleModalAddAnother = () => {
    setShowSuccessModal(false);
  };

  const handleModalNavigateToSiblings = () => {
    setShowSuccessModal(false);
    moveToNextStep(5);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="form-step">
      <div className="row g-3">
        <div className="col-12">
          <div className="mb-3" style={{ width: "200px" }}>
            <label className="form-label form-label-new">
              Select Pupil <span className="text-danger fs-9">*</span>
            </label>
            <select
              className="form-select"
              onChange={handlePupilSelection}
              value={selectedPupilId || ""}
              disabled={isLoading}
            >
              <option value="">Select</option>
              {pupils.map((pupil) => (
                <option key={pupil.id} value={pupil.id}>
                  {pupil.firstName + " " + pupil.lastName}
                </option>
              ))}
            </select>
            {pupilError && (
              <small className="text-danger">This field is required.</small>
            )}
          </div>

          <h5 className="mb-3 p-2 px-3 text-white bg-brand rounded-pill">
            Previous School Information
          </h5>

          <div className="row g-2">
            <div className="col">
              <label className="form-label">School</label>
            </div>
            <div className="col">
              <label className="form-label">Entry Date</label>
            </div>
            <div className="col">
              <label className="form-label">Leaving Date</label>
            </div>
            <div className="col">
              <label className="form-label">Country</label>
            </div>
            <div className="col">
              <label className="form-label">Language of Instruction</label>
            </div>
            <div className="col-1">
              <label className="form-label">Action</label>
            </div>
          </div>

          {studentPreviousSchools?.map((field, index) => (
            <div className="row g-2 mb-2" key={field.id}>
              <div className="col">
                <input
                  type="text"
                  {...register(`studentPreviousSchools.${index}.otherName`)}
                  className="form-control"
                />
                {errors.studentPreviousSchools?.[index]?.otherName && (
                  <small className="text-danger">
                    {errors.studentPreviousSchools[index].otherName.message}
                  </small>
                )}
              </div>

              <div className="col">
                <input
                  type="date"
                  {...register(`studentPreviousSchools.${index}.dateOfEntry`)}
                  className="form-control"
                />
                {errors.studentPreviousSchools?.[index]?.dateOfEntry && (
                  <small className="text-danger">
                    {errors.studentPreviousSchools[index].dateOfEntry.message}
                  </small>
                )}
              </div>

              <div className="col">
                <input
                  type="date"
                  {...register(`studentPreviousSchools.${index}.dateOfLeaving`)}
                  className="form-control"
                />
                {errors.studentPreviousSchools?.[index]?.dateOfLeaving && (
                  <small className="text-danger">
                    {errors.studentPreviousSchools[index].dateOfLeaving.message}
                  </small>
                )}
              </div>

              <div className="col">
                <select
                  className="form-select"
                  {...register(`studentPreviousSchools.${index}.countryId`)}
                >
                  <option value="0">Select</option>
                  {countries.map((country) => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </select>
                {errors.studentPreviousSchools?.[index]?.countryId && (
                  <small className="text-danger">
                    {errors.studentPreviousSchools[index].countryId.message}
                  </small>
                )}
              </div>

              <div className="col">
                <input
                  type="text"
                  {...register(
                    `studentPreviousSchools.${index}.languageofInstruction`
                  )}
                  className="form-control"
                />
                {errors.studentPreviousSchools?.[index]
                  ?.languageofInstruction && (
                  <small className="text-danger">
                    {
                      errors.studentPreviousSchools[index].languageofInstruction
                        .message
                    }
                  </small>
                )}
              </div>
              <div className="col-1">
                <button
                  className="btn btn-outline-primary py-1 px-2 lh-2"
                  type="button"
                  onClick={() =>
                    appendPreviousSchoolField({
                      id: 0,
                      createdBy: localStorage.getItem("userId") || "",
                      updatedBy: localStorage.getItem("userId") || "",
                      schoolId: 0,
                      schoolName: "",
                      isNotExist: false,
                      dateOfEntry: "",
                      dateOfLeaving: "",
                      otherName: "",
                      countryId: "0",
                      languageofInstruction: "",
                      studentFirstRegistrationId: "",
                    })
                  }
                >
                  <span className="fas fa-plus"></span>
                </button>
                {studentPreviousSchools.length > 1 && (
                  <button
                    className="btn btn-outline-danger py-1 px-2 lh-2 ms-1"
                    type="button"
                    onClick={() => removePreviousSchoolField(index)}
                  >
                    <span className="fas fa-minus"></span>
                  </button>
                )}
              </div>
            </div>
          ))}

          <h5 className="mb-3 mt-4 p-2 px-3 text-white bg-brand rounded-pill">
            Other Details
          </h5>

          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label form-label-new">
                Previous School Curriculum
              </label>
              <div className="d-flex gap-2">
                <div className="w-100">
                  <select
                    {...register("previousSchoolCurriculum")}
                    className="form-select"
                  >
                    <option value="">Select</option>
                    {prevSchoolCurriculumData.map((item, index) => (
                      <option key={index} value={item.value}>
                        {item.value}
                      </option>
                    ))}
                  </select>
                </div>
                {["9", "10"].includes(roleId) ? null : (
                  <div>
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      style={{ padding: "5.5px 10px" }}
                      onClick={() =>
                        handleCustomDropdownSetting("previousSchoolCurriculum")
                      }
                    >
                      <i className="fas fa-plus"></i>
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-3">
              <label className="form-label form-label-new">
                If Other Curriculum, Please Specify
              </label>
              <input
                {...register("otherCurriculumn")}
                className="form-control"
              />
            </div>

            <div className="col-md-3">
              <label className="form-label form-label-new">
                Requires Special Educational Support?
              </label>
              <select
                {...register("educationalSupport")}
                className="form-select"
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label form-label-new">
                Any Physical/Learning/Behavioral Difficulties?
              </label>
              <select
                {...register("physicalDifficulties")}
                className="form-select"
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label form-label-new">
                Difficulties at Previous School?
              </label>
              <select
                {...register("priviousSchoolDifficulties")}
                className="form-select"
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label form-label-new">
                If Yes to Any of the Above, Provide Details
              </label>
              <textarea
                {...register("difficultyDetails")}
                className="form-control"
              />
            </div>

            <div className="col-md-3">
              <label className="form-label form-label-new">
                Family Circumstances to Be Aware Of
              </label>
              <textarea
                {...register("familyCircumstances")}
                className="form-control"
              />
            </div>

            <div className="col-md-3">
              <label className="form-label form-label-new">
                Any Siblings Applying to Nün Academy?
              </label>
              <select
                {...register("anySiblingInNunAcademy")}
                className="form-select"
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
                <option value="Not Yet">Not Yet</option>
              </select>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={onSaveForm}
            disabled={dataSubmitting}
          >
            {dataSubmitting ? (
              <span
                className="spinner-border spinner-custom-size me-1"
                aria-hidden="true"
              ></span>
            ) : null}
            Save Prev Schools
          </button>
        </div>
      </div>

      {isCustomDropdownValuesModalVisible &&
        createPortal(
          <DropdownWithCustomValuesModal
            dropdownName={customDropdownName}
            onClose={() => {
              setIsCustomDropdownValuesModalVisible(false);
              if (customDropdownName === "previousSchoolCurriculum") {
                fetchCommonSettings(
                  "previousSchoolCurriculum",
                  setPrevSchoolCurriculumData
                );
              }
            }}
          />,
          document.body
        )}

      <StepSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        onAddAnother={handleModalAddAnother}
        onNavigate={handleModalNavigateToSiblings}
        message="Previous Schools Saved Successfully!"
        nextButtonText="Go to Siblings"
      />
    </div>
  );
});

export default Step4PrevSchools;
